import { Component, OnInit, OnDestroy } from '@angular/core';
import { timeout } from 'rxjs/operators';
import * as XLSX from 'xlsx-js-style';

import { SetupBusinessStructureStoreSetupStore } from '../../../store';
import { HttpService, BreadcrumbService } from '../../../shared/services';
import { CompanyService,ConstantService } from '../../../shared/services';
import { UserHandlerService } from '../../../shared/services/user-handler-service';
import { TAGSCONSTS, TYPES } from '../../../constants';
import { autoFitColumns } from '../../../common-functions';

const CARD_TYPE_ORDER = ['AMEX', 'VISA/MC/DS', 'e-Check', 'Others'];
const TXN_PAGE_SIZE = 400;
const TXN_FETCH_CONCURRENCY = 5;
// xBatchID is required by the backend to fill xSettleDate/xSettleTime
const TXN_SELECT_FIELDS = [
    '_id', 'xStoreID', 'xTxnNum', 'xTxnActTag', 'xInvNum', 'xCustNum', 'xOrderNum', 'xBrandTag',
    'xMaskedAcctNum', 'xMainAmt', 'xIncSurcAmt', 'xIncTaxAmt', 'xTipAmt', 'xTtlAmt',
    'xAuthDate', 'xAuthTime', 'xBatchID',
].join(',');

@Component({
    selector: 'app-store-report',
    templateUrl: './store-report.component.html',
    styleUrls: ['./store-report.component.scss'],
})
export class StoreReportComponent implements OnInit, OnDestroy {
    businesses: any[] = [];
    stores: any[] = [];
    // Store dropdown items: all stores, or only those under the selected businesses
    storeOptions: any[] = [];
    selectedBusinessIDs: string[] = ['ALL'];
    allBusinessIDs: string[] = [];
    private prevBusinessIDs: string[] = ['ALL'];
    selectedStoreIDs: string[] = ['ALL'];
    private prevStoreIDs: string[] = ['ALL'];

    startDate: Date | null = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })();
    endDate: Date | null = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })();
    bsConfig = { dateInputFormat: 'MM/DD/YYYY', containerClass: 'theme-default' };

    isLoading = false;
    isExcelLoading = false;
    errorMessage = '';
    loadingProgress = '';

    private fetchGeneration = 0;
    private storesLoadFailed = false;

    visible = false;

    constructor(
        private httpService: HttpService,
        private companyService: CompanyService,
        private storeStore: SetupBusinessStructureStoreSetupStore,
        private breadcrumbService: BreadcrumbService,
        private constantService: ConstantService,
        public userHandlerService: UserHandlerService,
    ) {}

    ngOnInit(): void {
        this.setBreadcrumb();
        this.loadBusinesses();
    }

    openReportConfig(): void {
        this.visible = true;
    }

    configModalClosed(stateObj: any): void {
        this.visible = stateObj.modalVisibility;
    }

    ngOnDestroy(): void {
        this.fetchGeneration++;
    }

    setBreadcrumb(): void {
        const breadCrumb: any = {};
        breadCrumb.root = 'Reports';
        breadCrumb.child = 'Store Report';
        breadCrumb.subChild = '';
        breadCrumb.subChildLink = null;
        breadCrumb.grandChild = '';
        breadCrumb.rootList = [];
        this.breadcrumbService.setBreadCrumb(breadCrumb);
    }

    loadBusinesses(): void {
        const companyID = this.companyService.getcompanyID();
        if (!companyID) { return; }
        this.isLoading = true;
        this.fetchAllBusinessPages(
            { xSvcCompanyID: companyID, xStatus: 1, selectKey: '_id,xName' },
            1, []
        );
    }

    private fetchAllBusinessPages(baseParams: any, page: number, accumulated: any[]): void {
        this.httpService.getAll('xbcBusinesses', { ...baseParams, page, limit: 400 }).subscribe(
            (res: any) => {
                const batch: any[] = res?.data || [];
                const total: number = res?.meta?.totalRecords || 0;
                const combined = [...accumulated, ...batch];
                if (batch.length > 0 && combined.length < total) {
                    this.fetchAllBusinessPages(baseParams, page + 1, combined);
                } else {
                    this.businesses = [{ _id: 'ALL', xName: 'ALL' }, ...combined];
                    this.allBusinessIDs = combined.map(b => b._id);
                    this.loadStoresForBusinesses().finally(() => { this.isLoading = false; });
                }
            },
            () => {
                this.businesses = [{ _id: 'ALL', xName: 'ALL' }, ...accumulated];
                this.isLoading = false;
            },
        );
    }

    onBusinessChange(selected: string[]): void {
        if (!selected || selected.length === 0) {
            this.selectedBusinessIDs = ['ALL'];
        } else if (selected.includes('ALL')) {
            if (!this.prevBusinessIDs.includes('ALL')) {
                this.selectedBusinessIDs = ['ALL'];
            } else {
                this.selectedBusinessIDs = selected.filter(id => id !== 'ALL');
            }
        }
        this.prevBusinessIDs = [...this.selectedBusinessIDs];
        
        // this.stores = [{ _id: 'ALL', xName: 'ALL' }];
        this.selectedStoreIDs = ['ALL'];
        this.prevStoreIDs = ['ALL'];
        // this.loadStoresForBusinesses();
        this.refreshStoreOptions();
    }

    private refreshStoreOptions(): void {
        const actualBizIDs = this.selectedBusinessIDs.filter(id => id !== 'ALL');
        if (!actualBizIDs.length) {
            this.storeOptions = this.stores;
            return;
        }
        const bizSet = new Set(actualBizIDs.map(id => String(id)));
        this.storeOptions = [
            { _id: 'ALL', xName: 'ALL' },
            ...this.stores.filter(s => s._id !== 'ALL' && s.xBusinessID && bizSet.has(String(s.xBusinessID))),
        ];
    }

    private async withRetry<T>(fn: () => Promise<T>, retries = 2): Promise<T> {
        for (let attempt = 0; ; attempt++) {
            try {
                return await fn();
            } catch (err) {
                if (attempt >= retries) { throw err; }
                await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }
        }
    }

    // HttpService.getAll deletes page/limit from params, so always pass a copy
    private fetchStoresWithRetry(params: any): Promise<any> {
        return this.withRetry(() => this.storeStore.getAll({ ...params }).pipe(timeout(30000)).toPromise());
    }

    private async loadStoresForBusinesses(): Promise<void> {
        const targetIDs = this.allBusinessIDs;
        if (!targetIDs.length) { return; }
        const CHUNK_SIZE = 40;
        const CONCURRENCY = 4;
        const chunks: string[][] = [];
        for (let i = 0; i < targetIDs.length; i += CHUNK_SIZE) {
            chunks.push(targetIDs.slice(i, i + CHUNK_SIZE));
        }

        // fetchAll disables backend pagination (xbcStores caps $limit at 100),
        // so each chunk is a single request instead of a skip-based page loop
        const loadChunk = async (chunk: string[]): Promise<any[]> => {
            const params: any = { xStatus: 1, selectKey: '_id,xName,xBusinessID', fetchAll: 'true' };
            if (chunk.length === 1) {
                params.xBusinessID = chunk[0];
            } else {
                params.xBusinessIDList = chunk.join('|');
            }
            try {
                const res: any = await this.fetchStoresWithRetry(params);
                return res?.data || [];
            } catch (err) {
                this.storesLoadFailed = true;
                console.error('loadStoresForBusinesses failed', { chunk, err });
                return [];
            }
        };

        // Limit concurrent chunk requests to avoid backend timeouts
        this.storesLoadFailed = false;
        const results: any[][] = new Array(chunks.length);
        let next = 0;
        const worker = async () => {
            while (next < chunks.length) {
                const idx = next++;
                results[idx] = await loadChunk(chunks[idx]);
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, worker));

        this.stores = [{ _id: 'ALL', xName: 'ALL' }, ...results.flat()];
        this.refreshStoreOptions();
    }

    // Fetch names for stores missing from this.stores (e.g. chunks that failed to load)
    private async resolveStoreNames(storeIDs: string[]): Promise<Map<string, string>> {
        const nameMap = new Map<string, string>(this.stores.map(s => [s._id, s.xName]));
        const missing = storeIDs.filter(id => id !== 'Unknown' && !nameMap.has(id));
        const CHUNK_SIZE = 40;
        for (let i = 0; i < missing.length; i += CHUNK_SIZE) {
            const chunk = missing.slice(i, i + CHUNK_SIZE);
            try {
                const res: any = await this.fetchStoresWithRetry({
                    storeIds: chunk.join(','),
                    selectKey: '_id,xName',
                    page: 1,
                    limit: CHUNK_SIZE,
                });
                (res?.data || []).forEach((s: any) => {
                    nameMap.set(s._id, s.xName);
                    this.stores.push(s);
                });
            } catch (err) {
                console.error('resolveStoreNames failed', { chunk, err });
            }
        }
        return nameMap;
    }

    onStoreChange(selected: string[]): void {
        if (!selected || selected.length === 0) {
            this.selectedStoreIDs = ['ALL'];
        } else if (selected.includes('ALL')) {
            if (!this.prevStoreIDs.includes('ALL')) {
                this.selectedStoreIDs = ['ALL'];
            } else {
                this.selectedStoreIDs = selected.filter(id => id !== 'ALL');
            }
        }
        this.prevStoreIDs = [...this.selectedStoreIDs];
    }

    get dateRangeInvalid(): boolean {
        if (!this.startDate || !this.endDate) { return false; }
        const diffMs = new Date(this.endDate).getTime() - new Date(this.startDate).getTime();
        return diffMs < 0 || diffMs > 14 * 24 * 60 * 60 * 1000;
    }

    get datesRequired(): boolean {
        return !this.startDate || !this.endDate;
    }

    resetFilters(): void {
        this.fetchGeneration++;
        this.selectedBusinessIDs = ['ALL'];
        this.prevBusinessIDs = ['ALL'];
        this.selectedStoreIDs = ['ALL'];
        this.prevStoreIDs = ['ALL'];
        // this.stores = [{ _id: 'ALL', xName: 'ALL' }];
        this.refreshStoreOptions();
        this.startDate = null;
        this.endDate = null;
        this.isLoading = false;
        this.isExcelLoading = false;
        this.errorMessage = '';
        this.loadingProgress = '';
        // this.loadBusinesses();
    }

    async generateReport(): Promise<void> {
        if (this.datesRequired || this.dateRangeInvalid) { return; }
        this.isLoading = true;
        this.isExcelLoading = false;
        this.errorMessage = '';
        this.loadingProgress = 'Loading...';
        this.fetchGeneration++;
        const generation = this.fetchGeneration;

        try {
            // await this.loadStoresForBusinesses();
            // if (this.fetchGeneration !== generation) { return; }

            const txns = await this.fetchAllPagesIterative(generation);
            if (this.fetchGeneration !== generation) { return; }
            if (txns !== null) {                
                await this.exportExcel(txns);
            }
        } finally {
            if (this.fetchGeneration === generation) {
                this.isLoading = false;
                this.loadingProgress = '';
            }
        }
    }

    private fetchTxnPage(params: any, page: number): Promise<any> {
        return this.withRetry(() => this.httpService.getAll('xpeSettledTxns', {
            ...params,
            page,
            limit: TXN_PAGE_SIZE,
        }).pipe(timeout(60000)).toPromise());
    }

    private async fetchAllPagesIterative(generation: number): Promise<any[] | null> {
        let params: any = {
            xIsVoided: false,
            xRspStatus: 'success',
            selectKey: TXN_SELECT_FIELDS,
            filterField: 'xAuthDate',
            filterType: 'advancedDateFilter',
        };
        params = this.constantService.addClientTimeZone(params);
        const actualBizIDs = this.selectedBusinessIDs.filter(id => id !== 'ALL');
        if (actualBizIDs.length === 1) {
            params.xBusinessID = actualBizIDs[0];
        } else if (actualBizIDs.length > 1) {
            params.xBusinessIDList = actualBizIDs.join('|');
        }else{
            params.xWall1EntyID = this.companyService.getcompanyID();
        }

        const actualStoreIDs = this.selectedStoreIDs.filter(id => id !== 'ALL');
        if (actualStoreIDs.length === 1) {
            params.xStoreID = actualStoreIDs[0];
        } else if (actualStoreIDs.length > 1) {
            params.storeIDs = actualStoreIDs;
        }

        if (this.startDate) { params.startDate = this.startDate; }
        if (this.endDate) { params.endDate = this.endDate; }

        let total = 0;
        const pages: any[][] = [];
        try {
            const first: any = await this.fetchTxnPage(params, 1);
            if (this.fetchGeneration !== generation) { return null; }

            total = first?.meta?.totalRecords || 0;
            if (total > 100000) {
                this.errorMessage = `Too many records (${total.toLocaleString()}). Please narrow your date range or filter selection.`;
                return null;
            }
            pages[0] = first?.data || [];
            let loaded = pages[0].length;

            // Fetch remaining pages with a bounded number of concurrent requests
            const pageCount = Math.ceil(total / TXN_PAGE_SIZE);
            let nextPage = 2;
            const worker = async () => {
                while (nextPage <= pageCount && this.fetchGeneration === generation) {
                    const page = nextPage++;
                    const res: any = await this.fetchTxnPage(params, page);
                    pages[page - 1] = res?.data || [];
                    loaded += pages[page - 1].length;
                    if (this.fetchGeneration === generation) {
                        this.loadingProgress = `Loaded ${loaded.toLocaleString()} / ${total.toLocaleString()} records, please wait...`;
                    }
                }
            };
            await Promise.all(Array.from({ length: Math.min(TXN_FETCH_CONCURRENCY, pageCount - 1) }, worker));
            if (this.fetchGeneration !== generation) { return null; }
        } catch {
            if (this.fetchGeneration !== generation) { return null; }
            this.errorMessage = 'Report generation failed. Please try again.';
            return null;
        }

        // skip-based pages can overlap when sort keys tie, so dedupe by _id
        const seen = new Set<string>();
        const all: any[] = [];
        pages.forEach(batch => (batch || []).forEach(t => {
            if (seen.has(t._id)) { return; }
            seen.add(t._id);
            all.push(t);
        }));
        if (all.length < total) {
            console.warn(`fetchAllPagesIterative: expected ${total} records, got ${all.length}`);
        }
        return all;
    }

    private getCardTypeGroup(brandTag: string): string {
        if (brandTag === 'brand.cc.ax') { return 'AMEX'; }
        if (['brand.cc.vs', 'brand.cc.mc', 'brand.cc.ds'].includes(brandTag)) { return 'VISA/MC/DS'; }
        if (brandTag === 'brand.ec') { return 'e-Check'; }
        return 'Others';
    }

    private groupByCardType(txns: any[]): Record<string, any[]> {
        const result: Record<string, any[]> = {};
        txns.forEach(t => {
            const ct = this.getCardTypeGroup(t.xBrandTag || '');
            if (!result[ct]) { result[ct] = []; }
            result[ct].push(t);
        });
        return result;
    }

    private sumFieldCents(arr: any[], field: string): number {
        return arr.reduce((s, t) => s + (parseFloat(t[field]) || 0), 0) * 0.01;
    }

    private async exportExcel(txns: any[]): Promise<void> {
        this.isExcelLoading = true;
        const wb = XLSX.utils.book_new();

        const usedNames = new Set<string>();
        const safeSheetName = (name: string): string => {
            let base = name.substring(0, 31);
            let candidate = base;
            let i = 1;
            while (usedNames.has(candidate)) {
                const suffix = `(${i++})`;
                candidate = base.substring(0, 31 - suffix.length) + suffix;
            }
            usedNames.add(candidate);
            return candidate;
        };

        // Group transactions by store
        const storeMap = new Map<string, { storeName: string; txns: any[] }>();
        txns.forEach(t => {
            const key = t.xStoreID || 'Unknown';
            if (!storeMap.has(key)) {
                storeMap.set(key, { storeName: key, txns: [] });
            }
            storeMap.get(key).txns.push(t);
        });

        const nameMap = await this.resolveStoreNames([...storeMap.keys()]);
        let hasUnresolved = false;
        storeMap.forEach((entry, key) => {
            const name = nameMap.get(key);
            if (name) {
                entry.storeName = name;
            } else if (key !== 'Unknown') {
                hasUnresolved = true;
            }
        });
        if (hasUnresolved && this.storesLoadFailed) {
            this.errorMessage = 'Some store names could not be loaded; their IDs are shown instead.';
        }

        // ── Sheet 1: Summary ──────────────────────────────────────────
        const summaryRows: any[][] = [
            ['Store', 'Register', 'Total Revenue(Sale)', 'Total Expenses(Refund)', 'Net Total'],
        ];
        let grandRev = 0;
        let grandExp = 0;

        storeMap.forEach(({ storeName, txns: sTxns }) => {
            const grouped = this.groupByCardType(sTxns);
            CARD_TYPE_ORDER.forEach(ct => {
                const group = grouped[ct] || [];
                if (!group.length) { return; }
                const rev = group
                    .filter(t => t.xTxnActTag === 'txnact.purchase')
                    .reduce((s, t) => s + (parseFloat(t.xTtlAmt) || 0), 0) * 0.01;
                const exp = group
                    .filter(t => t.xTxnActTag === 'txnact.return')
                    .reduce((s, t) => s + (parseFloat(t.xTtlAmt) || 0), 0) * 0.01;
                grandRev += rev;
                grandExp += exp;
                summaryRows.push([
                    storeName, ct,
                    rev.toFixed(2),
                    exp.toFixed(2),
                    (rev - exp).toFixed(2),
                ]);
            });
        });
        summaryRows.push([
            'GRAND TOTAL', '',
            grandRev.toFixed(2),
            grandExp.toFixed(2),
            (grandRev - grandExp).toFixed(2),
        ]);

        const summaryWS = XLSX.utils.aoa_to_sheet(summaryRows);
        this.formatCell(summaryWS, summaryRows, 2, 4);
        autoFitColumns(summaryWS, summaryRows, { currencyColumns: [2, 3, 4] });
        this.styleHeaderRow(summaryWS, summaryRows[0].length);
        XLSX.utils.book_append_sheet(wb, summaryWS, safeSheetName('Summary'));

        // ── Per-store sheets ──────────────────────────────────────────
        for (const [, { storeName, txns: sTxns }] of storeMap) {
            const rows: any[][] = [];
            rows.push(['Transaction #','Type', 'Invoice #', 'Customer #', 'Order #','Card', 'Account #', 'Last 4 CC','MainAmt', 'SurChg', 'Tip', 'TtlAmt', 'MerAmt', 'Auth Date', 'Batch Date']);

            let storeMainAmt = 0;
            let storeSurcAmt = 0;
            let storeTipAmt = 0;
            let storeTtlAmt = 0;
            
            this.updateTransactionData(sTxns);

            sTxns.forEach((t: any) => {
                rows.push([
                    t.xTxnNum || '',
                    t.xTxnActTag || '',
                    t.xInvNum || '',
                    t.xCustNum || '',
                    t.xOrderNum || '',
                    // t.xMdmTag || '',
                    t.xBrandTag || '',
                    t.xMaskedAcctNum || '',
                    t.last4 || '',
                    t.xMainAmt || 0,
                    t.xIncSurcAmt || 0,
                    t.xTipAmt || 0,
                    t.xTtlAmt || 0,
                    t.xMerAmt || 0,
                    t.xAuthDateTime || '',
                    t.xSettleDateTime || '',
                ]);
                // storeMainAmt += (parseFloat(t.xMainAmt) || 0) * 0.01;
                // storeSurcAmt += (parseFloat(t.xIncSurcAmt) || 0) * 0.01;
                // storeTipAmt  += (parseFloat(t.xTipAmt) || 0) * 0.01;
                // storeTtlAmt  += (parseFloat(t.xTtlAmt) || 0) * 0.01;
            });
            // const storeMerAmt = storeTtlAmt - storeSurcAmt;
            // rows.push([
            //     'GRAND TOTAL', '', '', '', '', '',
            //     '$' + storeMainAmt.toFixed(2),
            //     '$' + storeSurcAmt.toFixed(2),
            //     '$' + storeTipAmt.toFixed(2),
            //     '$' + storeTtlAmt.toFixed(2),
            //     '$' + storeMerAmt.toFixed(2),
            //     '', '',
            // ]);
            const ws = XLSX.utils.aoa_to_sheet(rows);
            this.formatCell(ws, rows, 8, 12);
            autoFitColumns(ws, rows, { currencyColumns: [8, 9, 10, 11, 12] });
            this.styleHeaderRow(ws, rows[0].length);
            XLSX.utils.book_append_sheet(wb, ws, safeSheetName(storeName));
            
        }

        XLSX.writeFile(wb, `store-report.xlsx`);
        this.isExcelLoading = false;
    }

    private styleHeaderRow(ws: XLSX.WorkSheet, colCount: number) {
        for (let C = 0; C < colCount; C++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
            if (ws[cellAddress]) {
                ws[cellAddress].s = {
                    font: { bold: true },
                    fill: { fgColor: { rgb: 'D9D9D9' } },
                };
            }
        }
    }

    private formatCell(ws:XLSX.WorkSheet, rows:any[][], columnIndexbegin:number,columnIndexEnd:number) {
        for (let R = 1; R < rows.length; R++) {
            for (let C = columnIndexbegin; C <= columnIndexEnd; C++) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                if (ws[cellAddress]) {
                    ws[cellAddress].t = 'n';
                    ws[cellAddress].z = '$#,##0.00';
                }
            }
        }
    }

    private formatTxnActionTag(element: any): void {
        Object.keys(TAGSCONSTS.transactionActionTags.txnActionItemTags).forEach(key => {
            if (element.xTxnActTag === TAGSCONSTS.transactionActionTags.txnActionItemTags[key]) {
                element.xTxnActTag = TYPES.txnActionItem[key];
            }
        });
    }

    private formatBrandTag(element: any): void {
        Object.keys(TAGSCONSTS.brandTags.brandItemTags).forEach(key => {
            if (element.xBrandTag === TAGSCONSTS.brandTags.brandItemTags[key]) {
                element.xBrandTag = TYPES.brandItemTagsSC[key];
            }
        });
    }

    private formatMaskedAcctNum(element: any): void {
        if (element.xMaskedAcctNum.length >= 10) {
            element.first6 = element.xMaskedAcctNum?.substring(0, 6);
            element.last4 = element.xMaskedAcctNum?.substring(element.xMaskedAcctNum.length - 4);
            element.xMaskedAcctNum = element.first6 + '...' + element.last4;
        } else {
            element.first6 = '';
            element.last4 = '';
            element.xMaskedAcctNum = '';
        }
    }

    private formatAmounts(element: any): void {
        const totalAmt = element.xTtlAmt;
        const surchargeAmt = element.xIncSurcAmt;
        element.xMainAmt = element.xMainAmt ? (parseInt(element.xMainAmt) / 100).toFixed(2) : 0;
        element.xIncTaxAmt = element.xIncTaxAmt ? (parseInt(element.xIncTaxAmt) / 100).toFixed(2) : 0;
        element.xIncSurcAmt = element.xIncSurcAmt ? (parseInt(element.xIncSurcAmt) / 100).toFixed(2) : 0;
        element.xTipAmt = element.xTipAmt ? (parseInt(element.xTipAmt) / 100).toFixed(2) : 0;
        element.xTtlAmt = element.xTtlAmt ? (parseInt(element.xTtlAmt) / 100).toFixed(2) : 0;
        element.xMerAmt = (totalAmt && surchargeAmt) ? ((parseInt(totalAmt) - parseInt(surchargeAmt)) / 100).toFixed(2) : element.xTtlAmt;
    }

    private formatDates(element: any): void {
        element.xAuthDateTime = this.constantService.updateDateAndTime(element.xAuthTime, element.xAuthDate);
        element.xSettleDateTime = this.constantService.updateDateAndTime(element.xSettleTime, element.xSettleDate);
    }

    private updateTransactionData(data: any[]): void {
        data.forEach(element => {
            this.formatTxnActionTag(element);
            this.formatBrandTag(element);
            this.formatMaskedAcctNum(element);
            this.formatAmounts(element);
            this.formatDates(element);
        });
    }

}
